// Owner-only Health Status page. Surfaces MongoDB latency, integration modes,
// scheduler state, push subscription counts, and any pending pre-launch
// warnings (e.g. admin seed still at defaults).

import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import {
  Database, Zap, Bell, AlertTriangle, CheckCircle2,
  Loader2, RefreshCw, ShieldCheck,
} from "lucide-react";

const StatCard = ({ testId, icon: Icon, label, value, tone = "ink", hint }) => (
  <div
    data-testid={testId}
    className={`warm-card p-5 ${tone === "warn" ? "ring-1 ring-hearth-coral/30" : ""}`}
  >
    <div className="flex items-center gap-2 caption mb-2">
      <Icon size={12} />
      {label}
    </div>
    <div className={`font-display text-2xl leading-tight ${tone === "warn" ? "text-hearth-coral" : tone === "ok" ? "text-hearth-sageDark" : "text-hearth-ink"}`}>
      {value}
    </div>
    {hint && <div className="text-xs text-hearth-mute mt-1.5">{hint}</div>}
  </div>
);

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/status");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load status.");
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { setRefreshing(true); load(); };

  if (loading) {
    return (
      <div className="min-h-[40vh] grid place-items-center">
        <Loader2 className="animate-spin text-hearth-mute" size={22} />
      </div>
    );
  }
  if (!data) return null;

  const db = data.database || {};
  const sched = data.scheduler || {};
  const ints = data.integrations || {};
  const push = data.push || {};
  const warn = data.warnings || {};
  const counts = data.counts || {};

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Owner only · read-only"
        title="Health status"
        subtitle="A quick check that everything is humming behind the scenes."
        action={
          <button
            data-testid="status-refresh"
            onClick={refresh}
            disabled={refreshing}
            className="pill-btn rounded-full h-10 px-4 bg-hearth-clay hover:bg-hearth-clayDark text-white text-sm inline-flex items-center gap-2"
          >
            {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        }
      />

      {warn.admin_seed && (
        <div
          data-testid="status-warning-admin-seed"
          className="rounded-2xl border border-hearth-coral/40 bg-hearth-coral/8 px-5 py-4 flex items-start gap-3"
        >
          <AlertTriangle className="text-hearth-coral shrink-0 mt-0.5" size={18} />
          <div>
            <p className="font-medium text-hearth-ink">Admin seed warning</p>
            <p className="text-sm text-hearth-mute mt-1">{warn.admin_seed}</p>
          </div>
        </div>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          testId="status-mongo"
          icon={Database}
          label="MongoDB"
          value={db.ok ? `${db.ping_ms} ms` : "unreachable"}
          tone={db.ok ? "ok" : "warn"}
          hint={db.ok ? "Round-trip ping OK." : "Database ping failed — check MONGO_URL."}
        />
        <StatCard
          testId="status-scheduler"
          icon={Zap}
          label="Daily digest"
          value={sched.running ? "Running" : "Stopped"}
          tone={sched.running ? "ok" : "warn"}
          hint={sched.running ? `Next fire: ${fmt(sched.next_daily_digest)}` : "APScheduler is not running."}
        />
        <StatCard
          testId="status-push"
          icon={Bell}
          label="Web push"
          value={push.mode === "real" ? "Real (VAPID)" : "Mock"}
          tone={push.mode === "real" ? "ok" : "ink"}
          hint={
            push.mode === "real"
              ? `${push.subscriptions_in_household} subscription(s) in this household.`
              : "VAPID keys not set — pushes log server-side only."
          }
        />
        <StatCard
          testId="status-llm"
          icon={ShieldCheck}
          label="LLM (Emergent)"
          value={ints.llm_configured ? "Configured" : "Missing key"}
          tone={ints.llm_configured ? "ok" : "warn"}
          hint={ints.llm_configured ? "Claude Vision + text ready." : "Set EMERGENT_LLM_KEY in .env."}
        />
        <StatCard
          testId="status-resend"
          icon={CheckCircle2}
          label="Email (Resend)"
          value={ints.resend_configured ? "Configured" : "Missing key"}
          tone={ints.resend_configured ? "ok" : "ink"}
          hint={ints.resend_configured ? "Transactional email available." : "Set RESEND_API_KEY to send emails."}
        />
        <StatCard
          testId="status-places"
          icon={CheckCircle2}
          label="Google Places"
          value={ints.google_places_configured ? "Configured" : "Not set"}
          tone={ints.google_places_configured ? "ok" : "ink"}
          hint={ints.google_places_configured ? "Nearby vendor lookup available." : "Vendor auto-lookup will fall back to manual entry."}
        />
      </section>

      <section className="warm-card p-6" data-testid="status-counts">
        <h3 className="font-display text-2xl text-hearth-ink mb-4">Your household</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {[
            ["Members", counts.household_members],
            ["Grocery + pantry", counts.grocery_items],
            ["Bills", counts.bills],
            ["Vendors", counts.vendors],
            ["Warranties", counts.warranties],
          ].map(([label, n]) => (
            <div key={label} className="text-center">
              <div className="font-display text-3xl text-hearth-clay">{n ?? 0}</div>
              <div className="caption mt-1">{label}</div>
            </div>
          ))}
        </div>
      </section>

      <p className="text-xs text-hearth-mute text-center italic">
        Snapshot generated {fmt(data.generated_at)} · timezone {data.app?.timezone}
      </p>
    </div>
  );
}
